import { catchError, mergeMap, throwError } from "rxjs";
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateToken, Store, type StateContext } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import { ApiControlService } from '@medsurf/flat-services';
import * as FlatStates from '../../internal';
import { EntityBaseState } from '../base/entity.base.state';

export const USER_ENTITY_TOKEN = new StateToken<UserEntityStateModel>(FlatModels.EntityControlModels.EntityQueryType.enum.USER_ENTITY);

export type UserEntityStateModel = FlatModels.EntityStatesModels.EntityStateModel<FlatModels.UserEntityModels.User>;

@State<UserEntityStateModel>({
  name: USER_ENTITY_TOKEN,
  defaults: {
    entities: {},
  }
})
@Injectable()
export class UserEntityState extends EntityBaseState<FlatModels.UserEntityModels.User, UserEntityStateModel>{
  constructor(protected store: Store,
              protected apiControlService: ApiControlService) {
    super();
  }

  //<editor-fold desc="Selectors">
	
  @Selector([UserEntityState])
  static override typedEntities$(state: UserEntityStateModel): { [id: string]: FlatModels.UserEntityModels.User } {
    return EntityBaseState.typedEntities$(state);
  }
	
  @Selector([UserEntityState.typedEntities$])
  static override entities$(typedEntities: { [id: string]: FlatModels.UserEntityModels.User }): FlatModels.UserEntityModels.User[] {
    return EntityBaseState.entities$(typedEntities);
  }
	
  @Selector([UserEntityState.typedEntities$])
  static override entity$(typedEntities: { [id: string]: FlatModels.UserEntityModels.User }) {
    return EntityBaseState.entity$(typedEntities);
  }
	
  @Selector([UserEntityState.typedEntities$])
  static override entitiesByIds$(typedEntities: { [id: string]: FlatModels.UserEntityModels.User }) {
    return EntityBaseState.entitiesByIds$(typedEntities);
  }
	
  @Selector([UserEntityState.entities$])
  static usersArray$(entities: FlatModels.UserEntityModels.User[]) {
    return entities && entities.map(u => { return { value: u.id, name: `${u.lastName} ${u.firstName} (${u.email})`}; });
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  //<editor-fold desc="Insertion">
	
  @Action(FlatActions.UserEntityActions.GetEntitySuccess)
  override getEntitySuccess(state: StateContext<UserEntityStateModel>,
                            action: FlatActions.UserEntityActions.GetEntitySuccess) {
    return super.getEntitySuccess(state, action);
  }
	
  @Action(FlatActions.UserEntityActions.GetEntitiesSuccess)
  override getEntitiesSuccess(state: StateContext<UserEntityStateModel>,
                              action: FlatActions.UserEntityActions.GetEntitiesSuccess) {
    return super.getEntitiesSuccess(state, action);
  }
	
  @Action(FlatActions.UserEntityActions.ModifyEntitySuccess)
  override modifyEntitySuccess(state: StateContext<UserEntityStateModel>,
                               action: FlatActions.UserEntityActions.ModifyEntitySuccess) {
    return super.modifyEntitySuccess(state, action);
  }
	
  @Action(FlatActions.UserEntityActions.ModifyEntitiesSuccess)
  override modifyEntitiesSuccess(state: StateContext<UserEntityStateModel>,
                                 action: FlatActions.UserEntityActions.ModifyEntitiesSuccess) {
    return super.modifyEntitiesSuccess(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Edit">
	
  @Action(FlatActions.UserEntityActions.AddEntity)
  addEntity(_: StateContext<UserEntityStateModel>,
            {entity}: FlatActions.UserEntityActions.AddEntity) {
    // Check Id
    if (!entity.id) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.entity_has_no_id',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.UserEntityState.addEntity"
      );
    }

    // Add User
    return this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.saving)).pipe(
      mergeMap(() =>
        this.apiControlService.sendMessage(
          new FlatActions.HistoryControlActions.SaveSnapshotsRequest([{
            stateToken: FlatModels.EntityControlModels.EntityQueryType.enum.USER_ENTITY,
            modifierType: FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD,
            entity: entity,
            id: null,
            snapshotId: null
          }])
        )
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none))
      ),
      catchError((result) => {
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none));
        
        // Return error
        return throwError(() => {
          return result;
        });
      }),
    );
  }
	
  @Action(FlatActions.UserEntityActions.UpdateEntity)
  updateEntity(_: StateContext<UserEntityStateModel>,
               {entity}: FlatActions.UserEntityActions.UpdateEntity) {
    // Check Id
    if (!entity.id) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.entity_has_no_id',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.UserEntityState.updateEntity"
      );
    }

    // Update User
    return this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.saving)).pipe(
      mergeMap(() =>
        this.apiControlService.sendMessage(
          new FlatActions.HistoryControlActions.SaveSnapshotsRequest([{
            stateToken: FlatModels.EntityControlModels.EntityQueryType.enum.USER_ENTITY,
            modifierType: FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE,
            entity: entity,
            id: null,
            snapshotId: null
          }])
        )
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none))
      ),
      catchError((result) => {
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none));
        
        // Return error
        return throwError(() => {
          return result;
        });
      }),
    );
  }
	
  @Action(FlatActions.UserEntityActions.RemoveEntity)
  removeEntity(_: StateContext<UserEntityStateModel>,
               {entity}: FlatActions.UserEntityActions.RemoveEntity) {
    // Check Id
    if (!entity.id) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.entity_has_no_id',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.UserEntityState.removeEntity"
      );
    }

    // Remove User
    return this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.saving)).pipe(
      mergeMap(() =>
        this.apiControlService.sendMessage(
          new FlatActions.HistoryControlActions.SaveSnapshotsRequest([{
            stateToken: FlatModels.EntityControlModels.EntityQueryType.enum.USER_ENTITY,
            modifierType: FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE,
            entity: entity,
            id: null,
            snapshotId: null
          }])
        )
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none))
      ),
      catchError((result) => {
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none));
        
        // Return error
        return throwError(() => {
          return result;
        });
      }),
    );
  }

  //</editor-fold>

  //</editor-fold>
}
